import {api, ApiError} from '../../api.js';
import {brandDocumentTitle} from '../../brand.js';
import {apiUrl} from '../../config.js';
import {AuthSnapshot, currentAuth} from '../../auth.js';
import {fileTypeLabel, renderFileTypeIcon, renderFolderIcon} from '../fileTypeIcon.js';
import {escapeHtml, renderLayout} from '../../layout.js';

/** 空间文件条目 */
type SpaceFile = {
  id: string;
  taskId?: string;
  moduleId?: string;
  kind: string;
  folderId?: string;
  workspaceFolderId?: string;
  originalName: string;
  sizeBytes: number;
  mimeType?: string;
  status: string;
  uploadedBy?: string;
  uploadedByName?: string;
  templatePlanUsageCount?: number;
  isShared?: boolean;
  isSharedMirror?: boolean;
  sharedTargets?: Array<{shareId: string; enterpriseOrganizationId: string; enterpriseName?: string}>;
  createdAt: string;
};

/** 用户组织摘要 */
type UserOrganization = {
  id: string;
  name: string;
  organizationCode?: string;
  ownerUserId: string;
  role?: string;
  status?: string;
};

/** 空间范围 */
type SpaceScope = 'personal' | 'enterprise';

/** 工作区文件夹 */
type SpaceFolder = {
  id: string;
  name: string;
  parentId?: string;
  systemKey?: string;
  linkedTaskId?: string;
  linkedPlanId?: string;
  createdAt: string;
  updatedAt: string;
};

type ViewMode = 'list' | 'grid';
type PageMode = 'home' | 'library';
type EnterpriseZoneTab = 'shared' | 'uploaded';

/** 空间文件统计摘要 */
type SpaceSummary = {
  totalCount: number;
  totalBytes: number;
  folderCount: number;
  shownCount: number;
};

const SCOPE_STORAGE_KEY = 'tax-assistant-space-scope';
const VIEW_STORAGE_KEY = 'tax-assistant-space-view';
const ENTERPRISE_ZONE_STORAGE_KEY = 'tax-assistant-enterprise-zone-tab';
const RECENT_FILE_LIMIT = 20;
const MAX_UPLOAD_BYTES = 120 * 1024 * 1024;
const ENTERPRISE_SHARED_ROOT_KEY = 'enterprise-shared-root';
const PERSONAL_COMPLIANCE_ROOT_KEY = 'personal-compliance-root';
const ENTERPRISE_COMPLIANCE_ROOT_KEY = 'enterprise-compliance-root';
const ROOT_TREE_EXPANDED_KEY = '__root__';

/**
 * 判断组织是否为个人空间（无统一社会信用代码）
 * @param organization 组织摘要
 */
const isPersonalOrganization = (organization: Pick<UserOrganization, 'organizationCode'>): boolean => !String(organization.organizationCode ?? '').trim();

/**
 * 判断组织是否处于可用状态
 * @param organization 组织摘要
 */
const isActiveOrganization = (organization: UserOrganization): boolean => organization.status !== 'closed' && organization.status !== 'deleted';

/**
 * 根据当前会话组织推导空间范围
 * @param organization 当前登录组织
 */
const scopeFromOrganization = (organization: Pick<UserOrganization, 'organizationCode'>): SpaceScope => (
  isPersonalOrganization(organization) ? 'personal' : 'enterprise'
);

/**
 * 格式化文件大小
 * @param size 字节数
 */
const formatSize = (size: number): string => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

/**
 * 格式化时间
 * @param value ISO 时间
 */
const formatDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', {hour12: false});
};

/**
 * 渲染企业/个人空间主页面
 * @param root 页面根节点
 * @param auth 当前登录上下文
 */
export async function renderSpacePage(root: HTMLElement, initialAuth: AuthSnapshot): Promise<void> {
  let auth = (await currentAuth()) ?? initialAuth;
  renderLayout(root, auth, '<div class="space-drive-page"><section class="space-drive-loading">正在加载空间资料…</section></div>');
  const host = root.querySelector<HTMLElement>('.space-drive-page');
  if (!host) return;

  let organizations: UserOrganization[] = [];
  let files: SpaceFile[] = [];
  let folders: SpaceFolder[] = [];
  let currentFolderId = '';
  let viewMode: ViewMode = localStorage.getItem(VIEW_STORAGE_KEY) === 'grid' ? 'grid' : 'list';
  let pageMode: PageMode = 'home';
  let summary: SpaceSummary = {totalCount: 0, totalBytes: 0, folderCount: 0, shownCount: 0};
  let enterpriseSharedFiles: SpaceFile[] = [];
  let enterpriseUploadFiles: SpaceFile[] = [];
  let enterpriseZoneTab: EnterpriseZoneTab = localStorage.getItem(ENTERPRISE_ZONE_STORAGE_KEY) === 'shared' ? 'shared' : 'uploaded';
  let scope: SpaceScope = scopeFromOrganization(auth.organization);
  let message = '';
  let loading = true;
  let selectedFileIds = new Set<string>();
  let selectedFolderIds = new Set<string>();
  let expandedFolderIds = new Set<string>();

  const personalOrg = () => organizations
    .filter(item => isPersonalOrganization(item) && isActiveOrganization(item))
    .sort((left, right) => {
      if (left.ownerUserId === auth.user.id && right.ownerUserId !== auth.user.id) return -1;
      if (right.ownerUserId === auth.user.id && left.ownerUserId !== auth.user.id) return 1;
      return 0;
    })[0];
  const enterpriseOrgs = () => organizations.filter(item => !isPersonalOrganization(item) && isActiveOrganization(item));
  const enterpriseOrg = () => enterpriseOrgs().find(item => item.id === auth.organization.id) || enterpriseOrgs()[0];
  const canSwitchScope = () => Boolean(personalOrg() && enterpriseOrgs().length);
  const showScopeSwitcher = () => Boolean(personalOrg() || enterpriseOrgs().length);
  const isEnterpriseScope = () => !isPersonalOrganization(auth.organization);
  const currentScope = (): SpaceScope => scopeFromOrganization(auth.organization);
  const scopeBannerNote = (): string => {
    if (showScopeSwitcher()) return '点击右侧按钮可在个人空间与团队空间之间切换';
    return isEnterpriseScope() ? '当前账号仅可访问团队共享空间' : '当前账号仅可访问个人独立空间';
  };

  const folderPath = (): SpaceFolder[] => {
    const path: SpaceFolder[] = [];
    let cursor = currentFolderId;
    const guard = new Set<string>();
    while (cursor && !guard.has(cursor)) {
      guard.add(cursor);
      const folder = folders.find(item => item.id === cursor);
      if (!folder) break;
      path.unshift(folder);
      cursor = folder.parentId || '';
    }
    return path;
  };

  const isWorkspaceUpload = (file: SpaceFile): boolean => file.kind === 'workspace' && !file.taskId;

  /** 系统或业务关联目录不可重命名/删除 */
  const isFolderMutable = (folder: SpaceFolder): boolean => !folder.systemKey && !folder.linkedTaskId && !folder.linkedPlanId;

  const sharedRootFolder = (): SpaceFolder | undefined => folders.find(folder => folder.systemKey === ENTERPRISE_SHARED_ROOT_KEY);

  /** 团队空间根目录采用「共享文件 / 成员上传」分区展示 */
  const isEnterpriseRootView = (): boolean => isEnterpriseScope() && !currentFolderId;

  const childFolders = (): SpaceFolder[] => folders
    .filter(folder => (folder.parentId || '') === currentFolderId)
    .filter(folder => pageMode === 'library' || !isEnterpriseRootView() || folder.systemKey !== ENTERPRISE_SHARED_ROOT_KEY)
    .sort((a, b) => {
      if (a.systemKey === ENTERPRISE_SHARED_ROOT_KEY) return -1;
      if (b.systemKey === ENTERPRISE_SHARED_ROOT_KEY) return 1;
      return a.name.localeCompare(b.name, 'zh-CN');
    });

  /** 树形侧栏使用的子文件夹排序（包含企业共享根目录） */
  const treeChildFolders = (parentId = ''): SpaceFolder[] => folders
    .filter(folder => (folder.parentId || '') === parentId)
    .sort((a, b) => {
      if (a.systemKey === ENTERPRISE_SHARED_ROOT_KEY) return -1;
      if (b.systemKey === ENTERPRISE_SHARED_ROOT_KEY) return 1;
      return a.name.localeCompare(b.name, 'zh-CN');
    });

  /** 展开当前路径上的祖先文件夹，便于树侧栏定位 */
  const ensureExpandedToFolder = (folderId: string): void => {
    expandedFolderIds.add(ROOT_TREE_EXPANDED_KEY);
    let cursor = folderId;
    const guard = new Set<string>();
    while (cursor && !guard.has(cursor)) {
      guard.add(cursor);
      expandedFolderIds.add(cursor);
      const folder = folders.find(item => item.id === cursor);
      cursor = folder?.parentId || '';
    }
  };


  /** 统计直接子文件夹数量 */
  const childFolderCount = (folderId = ''): number => treeChildFolders(folderId).length;

  /** 合规制度库根目录允许在其下整理子文件夹。 */
  const isComplianceRootFolder = (folder: SpaceFolder): boolean =>
    folder.systemKey === PERSONAL_COMPLIANCE_ROOT_KEY || folder.systemKey === ENTERPRISE_COMPLIANCE_ROOT_KEY;

  /** 合规制度库下的普通目录可直接级联删除其中上传的制度文件。 */
  const isComplianceLibraryChildFolder = (folder: SpaceFolder): boolean => {
    let cursor = folder.parentId ? folders.find(item => item.id === folder.parentId) : undefined;
    const visited = new Set<string>();
    while (cursor && !visited.has(cursor.id)) {
      visited.add(cursor.id);
      if (isComplianceRootFolder(cursor)) return true;
      cursor = cursor.parentId ? folders.find(item => item.id === cursor!.parentId) : undefined;
    }
    return false;
  };

  /** 是否允许在指定父目录下新建文件夹 */
  const canCreateFolderIn = (parentId = ''): boolean => {
    if (!parentId) return true;
    const parent = folders.find(item => item.id === parentId);
    if (!parent) return true;
    if (isComplianceRootFolder(parent)) return true;
    return !parent.systemKey && !parent.linkedTaskId && !parent.linkedPlanId;
  };

  /** 文件夹类型说明 */
  const folderKindLabel = (folder: SpaceFolder): string => {
    if (folder.systemKey === ENTERPRISE_SHARED_ROOT_KEY) return '团队共享';
    if (folder.systemKey === PERSONAL_COMPLIANCE_ROOT_KEY || folder.systemKey === ENTERPRISE_COMPLIANCE_ROOT_KEY) return '合规制度库';
    if (folder.linkedTaskId) return '任务目录';
    if (folder.linkedPlanId) return '方案目录';
    if (folder.systemKey) return '系统目录';
    return '文件夹';
  };

  let activeFolderMenu: HTMLElement | null = null;

  /** 关闭文件夹操作菜单 */
  const closeFolderMenu = (): void => {
    activeFolderMenu?.remove();
    activeFolderMenu = null;
  };

  /** 创建文件夹 */
  const createFolder = async (parentId: string, name: string, options?: {silent?: boolean}): Promise<void> => {
    const result = await api.post<{folder: SpaceFolder}>(apiUrl('/api/files/workspace/folders'), {
      name: name.trim(),
      parentId,
    });
    folders = [...folders, result.folder];
    if (parentId) expandedFolderIds.add(parentId);
    else expandedFolderIds.add(ROOT_TREE_EXPANDED_KEY);
    expandedFolderIds.add(result.folder.id);
    if (!options?.silent) message = `已创建文件夹「${result.folder.name}」`;
  };

  /** 上传过程中的文件夹路径缓存，避免重复创建。 */
  const uploadFolderIdCache = new Map<string, string>();

  /**
   * 拆分浏览器返回的相对路径，得到目录层级与文件名。
   * @param relativePath 相对路径
   * @param fallbackName 无路径时的文件名
   */
  const splitRelativePath = (relativePath: string, fallbackName: string): {dirParts: string[]; fileName: string} => {
    const normalized = String(relativePath || fallbackName).replace(/\\/g, '/');
    const parts = normalized.split('/').filter(Boolean);
    const fileName = parts.pop() || fallbackName;
    return {dirParts: parts, fileName};
  };

  /**
   * 校验待上传文件体积，并过滤隐藏文件。
   * @param selected 待上传文件
   */
  const filterUploadableFiles = (selected: File[]): File[] | null => {
    const oversized = selected.find(file => file.size > MAX_UPLOAD_BYTES);
    if (oversized) {
      window.alert(`文件「${oversized.name}」超过 120MB，无法上传。`);
      return null;
    }
    return selected.filter(file => file.name && !file.name.startsWith('.'));
  };

  /**
   * 上传单个工作区文件到指定文件夹。
   * @param folderId 目标文件夹 ID
   * @param file 文件
   */
  const uploadWorkspaceFile = async (folderId: string, file: File): Promise<void> => {
    const result = await api.post<{file: SpaceFile}>(apiUrl('/api/files/upload-url'), {
      taskId: '',
      moduleId: 'common',
      kind: 'workspace',
      fileName: file.name,
      sizeBytes: file.size,
      mimeType: file.type || 'application/octet-stream',
      folderId,
    });
    await api.upload(apiUrl(`/api/files/${encodeURIComponent(result.file.id)}/content`), file, file.type || 'application/octet-stream');
  };

  /**
   * 按相对路径逐级创建文件夹，返回最终目录 ID。
   * @param baseParentId 起始父目录
   * @param dirParts 子目录名称列表
   */
  const resolveFolderId = async (baseParentId: string, dirParts: string[]): Promise<string> => {
    let parentId = baseParentId;
    const built: string[] = [];
    for (const part of dirParts) {
      built.push(part);
      const cacheKey = `${baseParentId}::${built.join('/')}`;
      const cached = uploadFolderIdCache.get(cacheKey);
      if (cached) {
        parentId = cached;
        continue;
      }
      const existing = folders.find(item => (item.parentId || '') === parentId && item.name === part);
      if (existing) {
        parentId = existing.id;
        uploadFolderIdCache.set(cacheKey, parentId);
        continue;
      }
      if (!canCreateFolderIn(parentId)) {
        throw new Error(`无法在路径「${built.join('/')}」下创建文件夹，当前目录不可新建。`);
      }
      await createFolder(parentId, part, {silent: true});
      const created = folders.find(item => (item.parentId || '') === parentId && item.name === part);
      if (!created) throw new Error(`无法创建文件夹「${part}」`);
      parentId = created.id;
      uploadFolderIdCache.set(cacheKey, parentId);
    }
    return parentId;
  };

  /**
   * 统计文件夹上传时选中的根目录数量（支持一次选择多个文件夹）。
   * @param selected 待上传文件
   */
  const countUploadRootFolders = (selected: File[]): number => {
    const roots = new Set<string>();
    for (const file of selected) {
      const relativePath = String((file as File & {webkitRelativePath?: string}).webkitRelativePath || '');
      const {dirParts} = splitRelativePath(relativePath, file.name);
      if (dirParts.length) roots.add(dirParts[0]);
    }
    return roots.size;
  };

  /**
   * 上传文件到当前目录；若包含相对路径则保留文件夹结构。
   * @param selected 待上传文件
   */
  const uploadWorkspaceFiles = async (selected: File[]): Promise<{uploaded: number; foldersCreated: number}> => {
    uploadFolderIdCache.clear();
    const foldersBefore = new Set(folders.map(item => item.id));
    let uploaded = 0;
    for (const file of selected) {
      const relativePath = String((file as File & {webkitRelativePath?: string}).webkitRelativePath || '');
      const {dirParts} = splitRelativePath(relativePath, file.name);
      const targetFolderId = dirParts.length ? await resolveFolderId(currentFolderId, dirParts) : currentFolderId;
      await uploadWorkspaceFile(targetFolderId, file);
      uploaded += 1;
    }
    const foldersCreated = folders.filter(item => !foldersBefore.has(item.id)).length;
    return {uploaded, foldersCreated};
  };

  let activeUploadMenu: HTMLElement | null = null;

  /** 关闭上传选项菜单 */
  const closeUploadMenu = (): void => {
    activeUploadMenu?.remove();
    activeUploadMenu = null;
  };

  /**
   * 递归读取拖入的目录项，生成带相对路径的文件列表。
   * @param entry 文件系统条目
   * @param parentPath 父路径前缀（不含末尾斜杠）
   */
  const readDroppedEntry = async (entry: FileSystemEntry, parentPath = ''): Promise<File[]> => {
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      const file = await new Promise<File>((resolve, reject) => {
        fileEntry.file(resolve, reject);
      });
      const relativePath = parentPath ? `${parentPath}/${file.name}` : file.name;
      try {
        Object.defineProperty(file, 'webkitRelativePath', {configurable: true, value: relativePath});
      } catch {
        /* 部分环境不可重写，仍可按文件名上传 */
      }
      return [file];
    }
    if (!entry.isDirectory) return [];
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    const children: FileSystemEntry[] = [];
    const readBatch = (): Promise<FileSystemEntry[]> => new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    // readEntries 可能需多次调用才能读完
    for (;;) {
      const batch = await readBatch();
      if (!batch.length) break;
      children.push(...batch);
    }
    const nextPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
    const nested: File[] = [];
    for (const child of children) {
      nested.push(...await readDroppedEntry(child, nextPath));
    }
    return nested;
  };

  /**
   * 从拖放事件收集文件（支持一次拖入多个文件夹）。
   * @param dataTransfer 拖放数据
   */
  const collectDroppedFiles = async (dataTransfer: DataTransfer): Promise<File[]> => {
    const items = Array.from(dataTransfer.items || []);
    if (items.length) {
      const entries = items
        .map(item => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
        .filter((entry): entry is FileSystemEntry => Boolean(entry));
      if (entries.length) {
        const files: File[] = [];
        for (const entry of entries) {
          files.push(...await readDroppedEntry(entry));
        }
        return files;
      }
    }
    return Array.from(dataTransfer.files || []);
  };

  /**
   * 打开上传选项菜单：选择文件或选择文件夹。
   * @param anchor 触发按钮
   */
  const openUploadMenu = (anchor: HTMLElement): void => {
    closeUploadMenu();
    closeFolderMenu();
    const rect = anchor.getBoundingClientRect();
    const menu = document.createElement('div');
    menu.className = 'space-drive-folder-menu space-drive-upload-menu';
    menu.setAttribute('role', 'menu');
    menu.style.top = `${rect.bottom + 6}px`;
    menu.style.left = `${Math.max(12, rect.right - 220)}px`;
    menu.innerHTML = `
      <button type="button" class="space-drive-folder-menu-item" data-upload-mode="files" role="menuitem">选择文件</button>
      <button type="button" class="space-drive-folder-menu-item" data-upload-mode="folders" role="menuitem">选择文件夹</button>
      <p class="space-drive-upload-menu-hint">多个文件夹：在资源管理器中按住 Ctrl 多选后，拖入页面即可集体上传</p>
    `;
    document.body.append(menu);
    activeUploadMenu = menu;
    menu.querySelector<HTMLButtonElement>('[data-upload-mode="files"]')?.addEventListener('click', () => {
      closeUploadMenu();
      host.querySelector<HTMLInputElement>('#spaceFileInput')?.click();
    });
    menu.querySelector<HTMLButtonElement>('[data-upload-mode="folders"]')?.addEventListener('click', () => {
      closeUploadMenu();
      if (!canCreateFolderIn(currentFolderId)) {
        window.alert('当前目录不可上传文件夹，请进入可整理的目录后再试。');
        return;
      }
      host.querySelector<HTMLInputElement>('#spaceFolderInput')?.click();
    });
    const dismiss = (event: MouseEvent) => {
      if (!menu.contains(event.target as Node) && event.target !== anchor) {
        closeUploadMenu();
        document.removeEventListener('click', dismiss, true);
      }
    };
    window.setTimeout(() => document.addEventListener('click', dismiss, true), 0);
  };

  /**
   * 切换上传按钮的加载状态。
   * @param uploading 是否上传中
   */
  const setUploadButtonsBusy = (uploading: boolean): void => {
    const uploadButton = host.querySelector<HTMLButtonElement>('#spaceUploadButton');
    if (uploadButton) {
      uploadButton.disabled = uploading;
      uploadButton.textContent = uploading ? '上传中…' : '上传';
    }
  };

  /** 重命名文件夹 */
  const renameFolder = async (folderId: string, name: string): Promise<void> => {
    const result = await api.patch<{folder: SpaceFolder}>(apiUrl(`/api/files/workspace/folders/${encodeURIComponent(folderId)}`), {name: name.trim()});
    folders = folders.map(item => item.id === folderId ? result.folder : item);
    message = `已重命名为「${result.folder.name}」`;
  };

  /**
   * 删除文件夹。
   * @param folder 目标文件夹
   * @param options.confirm 为 false 时跳过单项确认（供批量删除使用）
   */
  const deleteFolder = async (folder: SpaceFolder, options?: {confirm?: boolean}): Promise<void> => {
    const complianceLibrary = isComplianceLibraryChildFolder(folder);
    const detail = complianceLibrary
      ? '其中的制度文件与子文件夹将一并删除。'
      : '法务「合同文件审核」目录与审核项目将连同其中文件一并删除；其他文件夹需为空才可删除。';
    if (options?.confirm !== false && !window.confirm(`确定删除文件夹「${folder.name}」吗？${detail}`)) return;
    try {
      await api.delete(apiUrl(`/api/files/workspace/folders/${encodeURIComponent(folder.id)}`));
    } catch (error) {
      const text = error instanceof Error ? error.message : String(error);
      if (!/文件夹不存在|404/.test(text)) throw error;
    }
    folders = folders.filter(item => item.id !== folder.id);
    expandedFolderIds.delete(folder.id);
    if (currentFolderId === folder.id) currentFolderId = folder.parentId || '';
    message = `已删除文件夹「${folder.name}」`;
  };

  /**
   * 打开新建/重命名文件夹对话框
   * @param options 对话框参数
   */
  const openFolderNameDialog = (options: {
    mode: 'create' | 'rename';
    parentId?: string;
    folder?: SpaceFolder;
  }): void => {
    const isCreate = options.mode === 'create';
    const parentId = options.parentId ?? currentFolderId;
    const overlay = document.createElement('div');
    overlay.className = 'space-drive-import-overlay';
    overlay.innerHTML = `<div class="space-drive-import-dialog space-drive-folder-dialog" role="dialog" aria-modal="true" aria-label="${isCreate ? '新建文件夹' : '重命名文件夹'}">
      <div class="space-drive-import-head">
        <h2>${isCreate ? '新建文件夹' : '重命名文件夹'}</h2>
        <p>${isCreate ? '在当前位置创建一个新文件夹，用于整理资料。' : '修改文件夹名称，不会影响其中文件。'}</p>
      </div>
      <label class="space-drive-form-field">文件夹名称
        <input id="spaceFolderNameInput" class="space-drive-input" type="text" maxlength="80" value="${escapeHtml(options.folder?.name ?? '')}" placeholder="请输入文件夹名称">
      </label>
      <div class="space-drive-import-actions">
        <button type="button" class="space-drive-secondary-btn" data-folder-dialog-cancel>取消</button>
        <button type="button" class="space-drive-primary-btn" data-folder-dialog-confirm>${isCreate ? '创建' : '保存'}</button>
      </div>
    </div>`;
    document.body.append(overlay);
    const input = overlay.querySelector<HTMLInputElement>('#spaceFolderNameInput');
    const close = () => overlay.remove();
    input?.focus();
    if (isCreate) input?.select();
    overlay.querySelector('[data-folder-dialog-cancel]')?.addEventListener('click', close);
    overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
    overlay.querySelector('[data-folder-dialog-confirm]')?.addEventListener('click', async () => {
      const name = input?.value.trim() ?? '';
      if (!name) {
        window.alert('请输入文件夹名称。');
        input?.focus();
        return;
      }
      const button = overlay.querySelector<HTMLButtonElement>('[data-folder-dialog-confirm]');
      if (button) {
        button.disabled = true;
        button.textContent = isCreate ? '创建中…' : '保存中…';
      }
      try {
        if (isCreate) await createFolder(parentId, name);
        else if (options.folder) await renameFolder(options.folder.id, name);
        close();
        await loadWorkspaceData();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : (isCreate ? '创建文件夹失败' : '重命名失败'));
        if (button) {
          button.disabled = false;
          button.textContent = isCreate ? '创建' : '保存';
        }
      }
    });
    input?.addEventListener('keydown', event => {
      if (event.key === 'Enter') overlay.querySelector<HTMLButtonElement>('[data-folder-dialog-confirm]')?.click();
      if (event.key === 'Escape') close();
    });
  };

  /**
   * 计算文件夹距根节点的层级深度
   * @param folderId 文件夹 ID
   */
  const folderDepthFromRoot = (folderId: string): number => {
    let depth = 0;
    let parentId = folders.find(item => item.id === folderId)?.parentId || '';
    const guard = new Set<string>();
    while (parentId && !guard.has(parentId)) {
      guard.add(parentId);
      depth += 1;
      parentId = folders.find(item => item.id === parentId)?.parentId || '';
    }
    return depth;
  };

  /**
   * 渲染主列表文件夹前的层级缩进
   * @param folder 文件夹
   */
  const renderFolderListTreeGuides = (folder: SpaceFolder): string => {
    const depth = folderDepthFromRoot(folder.id);
    if (!depth) return '';
    return `<span class="space-drive-list-tree-indent" style="width:${depth * 16}px" aria-hidden="true"></span>`;
  };

  /**
   * 打开文件夹操作菜单
   * @param folderId 文件夹 ID
   * @param anchor 锚点按钮
   */
  const openFolderActionMenu = (folderId: string, anchor: HTMLElement): void => {
    closeUploadMenu();
    closeFolderMenu();
    const folder = folderId ? folders.find(item => item.id === folderId) : undefined;
    const rect = anchor.getBoundingClientRect();
    const menu = document.createElement('div');
    menu.className = 'space-drive-folder-menu';
    menu.style.top = `${rect.bottom + 6}px`;
    menu.style.left = `${Math.max(12, rect.right - 168)}px`;
    const canCreateChild = canCreateFolderIn(folderId);
    const mutable = folder ? isFolderMutable(folder) : false;
    const items: Array<{label: string; danger?: boolean; disabled?: boolean; title?: string; run: () => void}> = [
      {label: '打开', run: () => navigateToFolder(folderId)},
      {
        label: '新建子文件夹',
        disabled: !canCreateChild,
        title: canCreateChild ? '' : '系统或业务目录下不可新建',
        run: () => openFolderNameDialog({mode: 'create', parentId: folderId}),
      },
    ];
    if (folder) {
      items.push({
        label: '重命名',
        disabled: !mutable,
        title: mutable ? '' : '系统或业务目录不可重命名',
        run: () => openFolderNameDialog({mode: 'rename', folder}),
      });
      items.push({
        label: '删除',
        danger: true,
        disabled: !mutable,
        title: mutable ? '' : '系统或业务目录不可删除',
        run: () => void deleteFolder(folder).then(() => loadWorkspaceData()),
      });
    }
    menu.innerHTML = items.map((item, index) => `<button type="button" class="space-drive-folder-menu-item${item.danger ? ' is-danger' : ''}${item.disabled ? ' is-disabled' : ''}" data-folder-menu-action="${index}"${item.title ? ` title="${escapeHtml(item.title)}"` : ''} ${item.disabled ? 'disabled' : ''}>${escapeHtml(item.label)}</button>`).join('');
    document.body.append(menu);
    activeFolderMenu = menu;
    menu.querySelectorAll<HTMLButtonElement>('[data-folder-menu-action]').forEach(button => button.addEventListener('click', () => {
      const index = Number(button.dataset.folderMenuAction);
      const item = items[index];
      if (!item || item.disabled) return;
      closeFolderMenu();
      item.run();
    }));
    const dismiss = (event: MouseEvent) => {
      if (!menu.contains(event.target as Node) && event.target !== anchor) {
        closeFolderMenu();
        document.removeEventListener('click', dismiss, true);
      }
    };
    window.setTimeout(() => document.addEventListener('click', dismiss, true), 0);
  };

  /** 渲染路径面包屑 */
  const renderFolderBreadcrumb = (): string => {
    const crumbs = [{id: '', label: '全部文件'}, ...folderPath().map(folder => ({id: folder.id, label: folder.name}))];
    return crumbs.map((crumb, index) => {
      const current = index === crumbs.length - 1;
      return current
        ? `<span class="space-drive-path-crumb is-current">${escapeHtml(crumb.label)}</span>`
        : `<button type="button" class="space-drive-path-crumb" data-space-folder="${escapeHtml(crumb.id)}">${escapeHtml(crumb.label)}</button><span class="space-drive-path-sep" aria-hidden="true">/</span>`;
    }).join('');
  };

  /**
   * 递归渲染文件夹树节点
   * @param parentId 父文件夹 ID
   * @param depth 缩进层级
   */
  const renderFolderTreeNodes = (parentId: string, depth: number): string => treeChildFolders(parentId).map(folder => {
    const children = treeChildFolders(folder.id);
    const hasChildren = children.length > 0;
    const expanded = expandedFolderIds.has(folder.id);
    const active = currentFolderId === folder.id;
    const descendants = childFolderCount(folder.id);
    const sharedTag = folder.systemKey === ENTERPRISE_SHARED_ROOT_KEY ? '<span class="space-drive-tree-tag">共享</span>' : '';
    const toggle = hasChildren
      ? `<button type="button" class="space-drive-tree-toggle${expanded ? ' is-expanded' : ''}" data-tree-toggle="${escapeHtml(folder.id)}" aria-label="${expanded ? '收起' : '展开'} ${escapeHtml(folder.name)}"></button>`
      : '<span class="space-drive-tree-toggle is-spacer" aria-hidden="true"></span>';
    const menuButton = `<button type="button" class="space-drive-tree-menu-btn" data-folder-menu="${escapeHtml(folder.id)}" aria-label="${escapeHtml(folder.name)} 操作">⋯</button>`;
    return `<li class="space-drive-tree-item-wrap" role="none">
      <div class="space-drive-tree-row${active ? ' is-active' : ''}" data-tree-row="${escapeHtml(folder.id)}" style="--tree-depth:${depth}">
        ${toggle}
        <button type="button" class="space-drive-tree-item" data-space-folder="${escapeHtml(folder.id)}" role="treeitem" aria-selected="${active ? 'true' : 'false'}">
          ${renderFolderIcon(active || expanded, 'space-drive-tree-icon')}
          <span class="space-drive-tree-label" title="${escapeHtml(folder.name)}">${escapeHtml(folder.name)}${sharedTag}</span>
        </button>
        ${descendants ? `<span class="space-drive-tree-count">${descendants}</span>` : ''}
        ${menuButton}
      </div>
      ${hasChildren && expanded ? `<ul class="space-drive-tree-children" role="group">${renderFolderTreeNodes(folder.id, depth + 1)}</ul>` : ''}
    </li>`;
  }).join('');

  /** 渲染坚果云式文件夹树侧栏 */
  const renderFolderTreeSidebar = (): string => {
    const rootActive = !currentFolderId;
    const canCreate = canCreateFolderIn(currentFolderId);
    const rootChildCount = childFolderCount('');
    const rootExpanded = expandedFolderIds.has(ROOT_TREE_EXPANDED_KEY);
    const rootToggle = rootChildCount
      ? `<button type="button" class="space-drive-tree-toggle${rootExpanded ? ' is-expanded' : ''}" data-tree-toggle="${ROOT_TREE_EXPANDED_KEY}" aria-label="${rootExpanded ? '收起' : '展开'} 全部文件"></button>`
      : '<span class="space-drive-tree-toggle is-spacer" aria-hidden="true"></span>';
    return `<aside class="space-drive-tree-sidebar" aria-label="文件夹目录">
      <div class="space-drive-tree-toolbar">
        <span class="space-drive-tree-toolbar-title">文件夹</span>
        <button type="button" class="space-drive-tree-toolbar-btn" id="spaceTreeNewFolderButton" ${canCreate ? '' : 'disabled title="当前目录不可新建"'} aria-label="新建文件夹">+</button>
      </div>
      <ul class="space-drive-tree" role="tree">
        <li class="space-drive-tree-item-wrap" role="none">
          <div class="space-drive-tree-row${rootActive ? ' is-active' : ''}" data-tree-row="" style="--tree-depth:0">
            ${rootToggle}
            <button type="button" class="space-drive-tree-item" data-space-folder="" role="treeitem" aria-selected="${rootActive ? 'true' : 'false'}">
              ${renderFolderIcon(rootActive || rootExpanded, 'space-drive-tree-icon')}
              <span class="space-drive-tree-label">全部文件</span>
            </button>
            ${rootChildCount ? `<span class="space-drive-tree-count">${rootChildCount}</span>` : ''}
            <button type="button" class="space-drive-tree-menu-btn" data-folder-menu="" aria-label="全部文件 操作">⋯</button>
          </div>
          ${rootExpanded && rootChildCount ? `<ul class="space-drive-tree-children" role="group">${renderFolderTreeNodes('', 1)}</ul>` : ''}
        </li>
      </ul>
    </aside>`;
  };

  /** 渲染文件管理库中的文件夹行（与文件行同一列表） */
  const renderLibraryFolderRow = (folder: SpaceFolder, index: number): string => {
    const descendants = childFolderCount(folder.id);
    const mutable = isFolderMutable(folder);
    const personalChrome = usePersonalListChrome() || !isEnterpriseScope();
    const sharedTag = folder.systemKey === ENTERPRISE_SHARED_ROOT_KEY ? '<span class="space-drive-folder-tag">共享</span>' : '';
    const checked = selectedFolderIds.has(folder.id);
    return `<article class="space-drive-file-row space-drive-folder-row${personalChrome ? ' is-personal' : ' is-enterprise'}${checked ? ' is-selected' : ''}" data-space-folder="${escapeHtml(folder.id)}" role="button" tabindex="0">
      <label class="space-drive-file-check"><input type="checkbox" class="space-folder-select" data-folder-select="${escapeHtml(folder.id)}" ${checked ? 'checked' : ''} aria-label="选择文件夹 ${escapeHtml(folder.name)}"></label>
      <span class="space-drive-file-index" aria-hidden="true">${index + 1}</span>
      <div class="space-drive-file-main">
        ${renderFolderIcon(false, 'space-drive-file-icon')}
        <div class="space-drive-file-copy">
          <strong title="${escapeHtml(folder.name)}">${escapeHtml(folder.name)}${sharedTag}</strong>
          ${descendants ? `<small>${descendants} 个子文件夹</small>` : ''}
        </div>
      </div>
      ${personalChrome ? '<span class="space-drive-file-status">文件夹</span>' : '<span class="space-drive-file-uploader">—</span><span class="space-drive-file-time">—</span>'}
      <div class="space-drive-file-actions">
        ${mutable ? `<button type="button" class="space-drive-text-btn" data-folder-rename="${escapeHtml(folder.id)}">重命名</button><button type="button" class="space-drive-text-btn is-danger" data-folder-delete="${escapeHtml(folder.id)}">删除</button>` : '<span class="space-drive-muted">打开</span>'}
      </div>
    </article>`;
  };

  /** 渲染文件管理库统一列表（文件夹与文件混排，文件夹在前） */
  const renderLibraryContentList = (): string => {
    const folderItems = childFolders();
    const fileItems = visibleFiles();
    if (!folderItems.length && !fileItems.length) return '';
    const personalChrome = usePersonalListChrome() || !isEnterpriseScope();
    const rows: string[] = [];
    let index = 0;
    folderItems.forEach(folder => {
      rows.push(renderLibraryFolderRow(folder, index));
      index += 1;
    });
    fileItems.forEach(file => {
      rows.push(renderFileRow(file, index));
      index += 1;
    });
    return `<div class="space-drive-file-list space-drive-library-list ${personalChrome ? 'is-personal' : 'is-enterprise'}">${renderFileListHeader(true)}${rows.join('')}</div>`;
  };

  /** 渲染文件管理库网格视图（文件夹与文件同一区域） */
  const renderLibraryContentGrid = (): string => {
    const folderItems = childFolders();
    const fileItems = visibleFiles();
    if (!folderItems.length && !fileItems.length) return '';
    return `<div class="space-drive-file-grid space-drive-library-grid">${folderItems.map(renderFolderTile).join('')}${fileItems.map((file, index) => renderFileCard(file, index)).join('')}</div>`;
  };

  /**
   * 将团队空间文件拆分为共享文件与成员上传两组
   * @param sourceFiles 当前可见文件
   */
  const partitionEnterpriseFiles = (sourceFiles: SpaceFile[]): {shared: SpaceFile[]; uploaded: SpaceFile[]} => {
    const sharedFolderId = sharedRootFolder()?.id;
    const shared = sourceFiles.filter(file => file.isSharedMirror || (sharedFolderId && file.workspaceFolderId === sharedFolderId));
    const sharedIds = new Set(shared.map(file => file.id));
    const uploaded = sourceFiles.filter(file => !sharedIds.has(file.id));
    return {
      shared: shared.sort((left, right) => right.createdAt.localeCompare(left.createdAt)),
      uploaded: uploaded.sort((left, right) => right.createdAt.localeCompare(left.createdAt)),
    };
  };

  const syncEnterprisePartitions = (): void => {
    if (!isEnterpriseRootView()) {
      enterpriseSharedFiles = [];
      enterpriseUploadFiles = [];
      return;
    }
    const partitioned = partitionEnterpriseFiles(files);
    enterpriseSharedFiles = partitioned.shared;
    enterpriseUploadFiles = partitioned.uploaded;
  };

  const visibleFiles = (): SpaceFile[] => {
    const list = pageMode === 'library'
      ? files
      : files.filter(file => {
        if (isWorkspaceUpload(file)) return (file.workspaceFolderId || '') === currentFolderId;
        return currentFolderId === '';
      });
    return list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  };

  const canDeleteFile = (file: SpaceFile): boolean => {
    if (['legal-review', 'legal-review-annotated', 'legal-review-preview', 'legal-review-revised'].includes(file.kind)) {
      if (!file.uploadedBy) return true;
      return file.uploadedBy === auth.user.id;
    }
    if (file.taskId) return false;
    if (file.kind === 'template' && Number(file.templatePlanUsageCount ?? 0) > 0) return false;
    if (!file.uploadedBy) return true;
    const isManager = auth.organization.ownerUserId === auth.user.id || auth.role === 'enterprise_admin';
    if (isEnterpriseScope()) return file.uploadedBy === auth.user.id || isManager;
    return file.uploadedBy === auth.user.id;
  };

  const canMoveFile = (file: SpaceFile): boolean => {
    if (['customs', 'invoice'].includes(file.kind)) return false;
    if (file.taskId) return false;
    if (file.kind === 'template' && Number(file.templatePlanUsageCount ?? 0) > 0) return false;
    if (!file.uploadedBy) return true;
    const isManager = auth.organization.ownerUserId === auth.user.id || auth.role === 'enterprise_admin';
    if (isEnterpriseScope()) return file.uploadedBy === auth.user.id || isManager;
    return file.uploadedBy === auth.user.id;
  };

  const canShareFile = (file: SpaceFile): boolean => {
    if (isEnterpriseScope() || !enterpriseOrgs().length) return false;
    if (['customs', 'invoice'].includes(file.kind)) return false;
    if (!file.uploadedBy) return true;
    return file.uploadedBy === auth.user.id;
  };

  const canUploadToEnterprise = (file: SpaceFile): boolean => {
    if (isEnterpriseScope() || !enterpriseOrgs().length) return false;
    if (['customs', 'invoice'].includes(file.kind)) return false;
    if (file.taskId) return false;
    if (!file.uploadedBy) return true;
    return file.uploadedBy === auth.user.id;
  };

  const canDownloadFile = (file: SpaceFile): boolean => !['customs', 'invoice'].includes(file.kind);

  const sourceLabel = (file: SpaceFile): string => {
    if (file.taskId) return '任务生成';
    if (file.kind === 'workspace') return '手动上传';
    return '业务资料';
  };

  /**
   * 展示上传者名称
   * @param file 空间文件
   */
  const uploaderLabel = (file: SpaceFile): string => {
    if (file.uploadedByName) return file.uploadedByName;
    if (file.uploadedBy && file.uploadedBy === auth.user.id) return auth.user.displayName || '我';
    return '未知成员';
  };

  /**
   * 展示上传时间
   * @param file 空间文件
   */
  const uploadTimeLabel = (file: SpaceFile): string => formatDate(file.createdAt);

  /**
   * 渲染共享状态标识
   * @param file 空间文件
   */
  const renderSharedBadge = (file: SpaceFile): string => {
    if (file.isSharedMirror) {
      return '<span class="space-drive-shared-badge is-mirror" title="成员共享文件">共享</span>';
    }
    if (file.isShared) {
      const names = (file.sharedTargets ?? []).map(target => target.enterpriseName).filter(Boolean).join('、');
      return `<span class="space-drive-shared-badge" title="${escapeHtml(names ? `已共享至：${names}` : '已共享至团队空间')}">共享</span>`;
    }
    return '';
  };

  /** 构建文件夹选择列表（用于移动） */
  const folderPickerOptions = (sourceFolders: SpaceFolder[] = folders): Array<{id: string; label: string; disabled: boolean}> => {
    const options: Array<{id: string; label: string; disabled: boolean}> = [{id: '', label: '全部文件（根目录）', disabled: false}];
    const walk = (parentId: string, depth: number): void => {
      sourceFolders
        .filter(folder => (folder.parentId || '') === parentId)
        .sort((left, right) => {
          if (left.systemKey === ENTERPRISE_SHARED_ROOT_KEY) return -1;
          if (right.systemKey === ENTERPRISE_SHARED_ROOT_KEY) return 1;
          return left.name.localeCompare(right.name, 'zh-CN');
        })
        .forEach(folder => {
          const disabled = Boolean(folder.linkedTaskId || folder.linkedPlanId);
          const sharedNote = folder.systemKey === ENTERPRISE_SHARED_ROOT_KEY ? '（共享）' : '';
          options.push({
            id: folder.id,
            label: `${'　'.repeat(depth)}${folder.name}${sharedNote}`,
            disabled,
          });
          walk(folder.id, depth + 1);
        });
    };
    walk('', 0);
    return options;
  };

  /**
   * 读取团队空间文件夹列表
   * @param enterpriseOrganizationId 企业组织 ID
   */
  const loadEnterpriseFolders = async (enterpriseOrganizationId: string): Promise<SpaceFolder[]> => {
    const result = await api.get<{folders: SpaceFolder[]}>(apiUrl(`/api/files/workspace/folders?enterpriseOrganizationId=${encodeURIComponent(enterpriseOrganizationId)}`));
    return result.folders ?? [];
  };

  const activeEnterpriseZoneFiles = (): SpaceFile[] => (
    enterpriseZoneTab === 'shared' ? enterpriseSharedFiles : enterpriseUploadFiles
  );

  const activeEnterpriseZoneFolders = (): SpaceFolder[] => (
    enterpriseZoneTab === 'uploaded' ? childFolders() : []
  );

  /** 文件管理库内企业与个人空间使用同一套列表布局 */
  const usePersonalListChrome = (): boolean => pageMode === 'library';

  const useEnterpriseZoneLayout = (): boolean => false;

  const selectableFiles = (): SpaceFile[] => {
    if (useEnterpriseZoneLayout()) return activeEnterpriseZoneFiles();
    return visibleFiles();
  };

  const movableSelectedFiles = (): SpaceFile[] => selectableFiles().filter(file => selectedFileIds.has(file.id) && canMoveFile(file));

  const shareableSelectedFiles = (): SpaceFile[] => selectableFiles().filter(file => selectedFileIds.has(file.id) && canShareFile(file));

  const uploadableSelectedFiles = (): SpaceFile[] => selectableFiles().filter(file => selectedFileIds.has(file.id) && canUploadToEnterprise(file));

  const sharedSelectedFiles = (): SpaceFile[] => selectableFiles().filter(file => selectedFileIds.has(file.id) && file.isShared);

  const deletableSelectedFiles = (): SpaceFile[] => selectableFiles().filter(file => selectedFileIds.has(file.id) && canDeleteFile(file));

  /** 当前目录下可选中的子文件夹。 */
  const selectableFolders = (): SpaceFolder[] => childFolders();

  /** 已勾选且允许删除的文件夹。 */
  const deletableSelectedFolders = (): SpaceFolder[] => selectableFolders().filter(folder => selectedFolderIds.has(folder.id) && isFolderMutable(folder));

  /** 当前可见列表是否已全部勾选。 */
  const isAllVisibleSelected = (): boolean => {
    const files = selectableFiles();
    const folderItems = selectableFolders();
    if (!files.length && !folderItems.length) return false;
    return files.every(file => selectedFileIds.has(file.id)) && folderItems.every(folder => selectedFolderIds.has(folder.id));
  };

  const selectedItemCount = (): number => selectedFileIds.size + selectedFolderIds.size;

  const clearSelection = (): void => {
    selectedFileIds = new Set<string>();
    selectedFolderIds = new Set<string>();
  };

  const toggleFileSelection = (fileId: string, checked: boolean): void => {
    const next = new Set(selectedFileIds);
    if (checked) next.add(fileId);
    else next.delete(fileId);
    selectedFileIds = next;
    draw();
  };

  /**
   * 勾选或取消当前目录下的文件夹。
   * @param folderId 文件夹 ID
   * @param checked 是否选中
   */
  const toggleFolderSelection = (folderId: string, checked: boolean): void => {
    if (!folderId) return;
    const next = new Set(selectedFolderIds);
    if (checked) next.add(folderId);
    else next.delete(folderId);
    selectedFolderIds = next;
    draw();
  };

  const toggleSelectAllVisible = (checked: boolean): void => {
    if (!checked) {
      clearSelection();
      draw();
      return;
    }
    selectedFileIds = new Set(selectableFiles().map(file => file.id));
    selectedFolderIds = new Set(selectableFolders().map(folder => folder.id));
    draw();
  };


  const renderFolderTile = (folder: SpaceFolder): string => {
    const childCount = folders.filter(item => (item.parentId || '') === folder.id).length;
    const mutable = isFolderMutable(folder);
    const checked = selectedFolderIds.has(folder.id);
    const actionButtons = mutable
      ? `<span class="space-drive-folder-actions">
        <button type="button" class="space-drive-icon-btn" data-folder-rename="${escapeHtml(folder.id)}" title="重命名" aria-label="重命名文件夹">✎</button>
        <button type="button" class="space-drive-icon-btn is-danger" data-folder-delete="${escapeHtml(folder.id)}" title="删除" aria-label="删除文件夹">×</button>
      </span>`
      : '';
    return `<article class="space-drive-folder-card${checked ? ' is-selected' : ''}" data-space-folder="${escapeHtml(folder.id)}" role="button" tabindex="0">
      <label class="space-drive-file-card-check"><input type="checkbox" class="space-folder-select" data-folder-select="${escapeHtml(folder.id)}" ${checked ? 'checked' : ''} aria-label="选择文件夹 ${escapeHtml(folder.name)}"></label>
      ${renderFolderIcon(false)}
      <span class="space-drive-folder-meta">
        <strong title="${escapeHtml(folder.name)}">${escapeHtml(folder.name)}${folder.systemKey === ENTERPRISE_SHARED_ROOT_KEY ? '<span class="space-drive-folder-tag">共享</span>' : ''}</strong>
        ${childCount ? `<small>${childCount} 个子文件夹</small>` : ''}
      </span>
      ${actionButtons}
    </article>`;
  };

  const renderFileRow = (file: SpaceFile, index: number): string => {
    const deletable = canDeleteFile(file);
    const downloadable = canDownloadFile(file);
    const enterprise = isEnterpriseScope() && !usePersonalListChrome();
    const selectable = true;
    const checked = selectedFileIds.has(file.id);
    return `<article class="space-drive-file-row${enterprise ? ' is-enterprise' : ' is-personal'}${checked ? ' is-selected' : ''}" data-file-id="${escapeHtml(file.id)}">
      ${selectable ? `<label class="space-drive-file-check"><input type="checkbox" class="space-file-select" data-file-select="${escapeHtml(file.id)}" ${checked ? 'checked' : ''} aria-label="选择 ${escapeHtml(file.originalName)}"></label>` : ''}
      <span class="space-drive-file-index" aria-hidden="true">${index + 1}</span>
      <div class="space-drive-file-main">
        ${renderFileTypeIcon(file.originalName)}
        <div class="space-drive-file-copy">
          <strong title="${escapeHtml(file.originalName)}">${escapeHtml(file.originalName)}${renderSharedBadge(file)}</strong>
          <small>${escapeHtml(fileTypeLabel(file.originalName))} · ${formatSize(file.sizeBytes)} · ${escapeHtml(sourceLabel(file))}${enterprise ? '' : ` · ${escapeHtml(uploadTimeLabel(file))}${file.uploadedByName ? ` · ${escapeHtml(uploaderLabel(file))}` : ''}`}</small>
        </div>
      </div>
      ${enterprise ? `<span class="space-drive-file-uploader" title="${escapeHtml(uploaderLabel(file))}">${escapeHtml(uploaderLabel(file))}</span><time class="space-drive-file-time" datetime="${escapeHtml(file.createdAt)}">${escapeHtml(uploadTimeLabel(file))}</time>` : `<span class="space-drive-file-status">${file.status === 'uploaded' ? '已上传' : file.status === 'ready' ? '已生成' : escapeHtml(file.status)}</span>`}
      <div class="space-drive-file-actions">
        ${downloadable ? '<button type="button" class="space-drive-text-btn space-download">下载</button>' : '<span class="space-drive-muted">识别原件</span>'}
        ${deletable ? '<button type="button" class="space-drive-text-btn is-danger space-delete">删除</button>' : ''}
      </div>
    </article>`;
  };

  const renderFileListHeader = (libraryMixed = false): string => {
    const enterprise = isEnterpriseScope() && !usePersonalListChrome();
    const selectable = true;
    const allSelected = isAllVisibleSelected();
    const nameLabel = libraryMixed ? '名称' : '文件名';
    return `<div class="space-drive-file-list-head${enterprise ? ' is-enterprise' : ' is-personal'}${selectable ? ' has-selection' : ''}" aria-hidden="true">
      ${selectable ? `<label class="space-drive-file-check"><input type="checkbox" class="space-file-select-all" ${allSelected ? 'checked' : ''} aria-label="全选当前文件夹和文件"></label>` : ''}
      <span class="space-drive-file-list-head-index">序号</span>
      <span class="space-drive-file-list-head-name">${nameLabel}</span>
      ${enterprise ? '<span class="space-drive-file-list-head-uploader">上传者</span><span class="space-drive-file-list-head-time">上传时间</span>' : '<span class="space-drive-file-list-head-status">状态</span>'}
      <span class="space-drive-file-list-head-actions">操作</span>
    </div>`;
  };

  const renderFileCard = (file: SpaceFile, index: number): string => {
    const deletable = canDeleteFile(file);
    const downloadable = canDownloadFile(file);
    const enterprise = isEnterpriseScope();
    const checked = selectedFileIds.has(file.id);
    return `<article class="space-drive-file-card${checked ? ' is-selected' : ''}" data-file-id="${escapeHtml(file.id)}">
      <label class="space-drive-file-card-check"><input type="checkbox" class="space-file-select" data-file-select="${escapeHtml(file.id)}" ${checked ? 'checked' : ''} aria-label="选择 ${escapeHtml(file.originalName)}"></label>
      <span class="space-drive-file-card-index">${index + 1}</span>
      ${renderFileTypeIcon(file.originalName, 'space-drive-file-card-icon')}
      <strong title="${escapeHtml(file.originalName)}">${escapeHtml(file.originalName)}${renderSharedBadge(file)}</strong>
      <small>${formatSize(file.sizeBytes)} · ${escapeHtml(sourceLabel(file))}</small>
      ${enterprise ? `<div class="space-drive-file-card-meta"><span title="${escapeHtml(uploaderLabel(file))}">上传者：${escapeHtml(uploaderLabel(file))}</span><time datetime="${escapeHtml(file.createdAt)}">${escapeHtml(uploadTimeLabel(file))}</time></div>` : ''}
      <div class="space-drive-file-card-actions">
        ${downloadable ? '<button type="button" class="space-drive-text-btn space-download">下载</button>' : ''}
        ${deletable ? '<button type="button" class="space-drive-text-btn is-danger space-delete">删除</button>' : ''}
      </div>
    </article>`;
  };

  const scopeLabel = (): string => isEnterpriseScope() ? '团队空间' : '个人空间';

  /**
   * 渲染工具栏左侧标题区
   */
  const renderToolbarLeading = (): string => {
    if (selectedItemCount()) {
      return `<div class="space-drive-section-title"><h2>已选择 ${selectedItemCount()} 项</h2></div>`;
    }
    if (pageMode === 'library') {
      const folderItems = childFolders();
      const fileCount = visibleFiles().length;
      const meta = [
        folderItems.length ? `${folderItems.length} 个子文件夹` : '',
        fileCount ? `${fileCount} 个文件` : '',
      ].filter(Boolean).join(' · ') || '空文件夹';
      return `<div class="space-drive-path-bar">
        <nav class="space-drive-path-breadcrumb" aria-label="当前路径">${renderFolderBreadcrumb()}</nav>
        <p class="space-drive-path-meta">${meta}</p>
      </div>`;
    }
    if (useEnterpriseZoneLayout()) {
      return renderEnterpriseZoneTabs();
    }
    return '<div class="space-drive-section-title"><h2>最近文件</h2></div>';
  };

  /**
   * 渲染团队空间根目录分区标签
   */
  const renderEnterpriseZoneTabs = (): string => {
    const tabs: Array<{id: EnterpriseZoneTab; label: string; count: number}> = [
      {id: 'shared', label: '共享文件', count: enterpriseSharedFiles.length},
      {id: 'uploaded', label: '成员上传', count: enterpriseUploadFiles.length},
    ];
    return `<nav class="space-drive-zone-tabs" role="tablist" aria-label="团队空间分区">
      ${tabs.map(tab => `<button type="button" class="space-drive-zone-tab ${enterpriseZoneTab === tab.id ? 'is-active' : ''}" role="tab" aria-selected="${enterpriseZoneTab === tab.id ? 'true' : 'false'}" data-enterprise-zone="${tab.id}">${escapeHtml(tab.label)}<span class="space-drive-zone-tab-count">${tab.count}</span></button>`).join('')}
    </nav>`;
  };

  /**
   * 渲染工具栏操作按钮
   */
  const renderToolbarActions = (): string => {
    const hasSelection = selectedItemCount() > 0;
    const selectionActions = hasSelection
      ? `<button type="button" class="space-drive-secondary-btn" id="spaceMoveButton">移动到…</button>
        <button type="button" class="space-drive-secondary-btn is-danger" id="spaceDeleteSelectionButton">删除</button>
        <button type="button" class="space-drive-secondary-btn" id="spaceClearSelectionButton">取消选择</button>`
      : '';
    const libraryActions = pageMode === 'library'
      ? `<div class="space-drive-view-toggle" role="group" aria-label="视图切换">
          <button type="button" class="space-drive-view-btn ${viewMode === 'list' ? 'is-active' : ''}" data-space-view="list" title="列表视图">☰</button>
          <button type="button" class="space-drive-view-btn ${viewMode === 'grid' ? 'is-active' : ''}" data-space-view="grid" title="网格视图">▦</button>
        </div>
        <button type="button" class="space-drive-secondary-btn" id="spaceNewFolderButton">新建文件夹</button>
        ${isEnterpriseScope() ? '<button type="button" class="space-drive-secondary-btn" id="spaceImportPersonalButton">从个人空间移入</button>' : ''}`
      : '<button type="button" class="space-drive-secondary-btn" id="spaceOpenLibraryButton">文件管理库</button>';
    const enterprisePersonalActions = hasSelection && !isEnterpriseScope() && enterpriseOrgs().length
      ? `${sharedSelectedFiles().length ? '<button type="button" class="space-drive-secondary-btn" id="spaceCancelShareButton">取消共享</button>' : ''}<button type="button" class="space-drive-secondary-btn" id="spaceUploadEnterpriseButton">上传至团队空间</button><button type="button" class="space-drive-secondary-btn" id="spaceShareEnterpriseButton">共享</button>`
      : '';
    const uploadAction = hasSelection
      ? ''
      : '<button type="button" class="space-drive-primary-btn" id="spaceUploadButton" aria-haspopup="menu" aria-expanded="false">上传</button>';
    return `${selectionActions}${libraryActions}${enterprisePersonalActions}<input id="spaceFileInput" type="file" multiple hidden><input id="spaceFolderInput" type="file" multiple webkitdirectory directory hidden>${uploadAction}`;
  };

  /**
   * 渲染空间范围切换控件
   */
  const renderScopeSwitcher = (): string => {
    if (!showScopeSwitcher()) {
      return `<p class="space-drive-scope-note">${escapeHtml(scopeBannerNote())}</p>`;
    }
    return `<div class="space-drive-scope-switch" role="tablist" aria-label="空间范围切换">
      <button type="button" class="space-drive-scope-btn ${currentScope() === 'personal' ? 'is-active is-personal-scope' : ''}" data-space-scope="personal" ${personalOrg() ? '' : 'disabled title="正在准备个人空间"'}>个人空间</button>
      <button type="button" class="space-drive-scope-btn ${currentScope() === 'enterprise' ? 'is-active is-enterprise-scope' : ''}" data-space-scope="enterprise" ${enterpriseOrgs().length ? '' : 'disabled title="尚未加入团队空间"'}>团队空间</button>
    </div>`;
  };

  /**
   * 同步顶部导航栏与页面级空间标识
   */
  const syncScopeChrome = (): void => {
    const enterprise = isEnterpriseScope();
    const title = document.querySelector<HTMLElement>('.topbar-title');
    if (title) title.textContent = pageMode === 'library' ? '文件管理库' : '空间';
    document.body.classList.toggle('space-scope-enterprise', enterprise);
    document.body.classList.toggle('space-scope-personal', !enterprise);
    document.title = brandDocumentTitle(pageMode === 'library' ? '文件管理库' : '空间');
  };

  /**
   * 渲染团队空间根目录当前分区内容
   */
  const renderEnterpriseZoneContent = (): string => {
    const zoneFiles = activeEnterpriseZoneFiles();
    const zoneFolders = activeEnterpriseZoneFolders();
    const folderGrid = zoneFolders.length
      ? `<div class="space-drive-folder-grid space-drive-zone-folder-grid">${zoneFolders.map(renderFolderTile).join('')}</div>`
      : '';
    const folderSlot = zoneFolders.length
      ? `<div class="space-drive-zone-folder-slot">${folderGrid}</div>`
      : '<div class="space-drive-zone-folder-slot" hidden></div>';
    const fileList = zoneFiles.length
      ? `<div class="space-drive-file-list is-enterprise space-drive-zone-file-list">${renderFileListHeader()}${zoneFiles.map((file, index) => renderFileRow(file, index)).join('')}</div>`
      : '';
    const empty = !zoneFolders.length && !zoneFiles.length
      ? '<div class="space-drive-empty space-drive-empty-compact"><strong>暂无文件</strong></div>'
      : '';
    return `<div class="space-drive-enterprise-body">${folderSlot}${fileList}${empty}</div>`;
  };

  /**
   * 个人空间首页标题，使用当前用户名称。
   * @returns 形如「张三的空间」
   */
  const personalSpaceHeroTitle = (): string => {
    const name = (auth.user.displayName || '').trim() || (auth.user.email || '').split('@')[0] || '我';
    return `${name}的空间`;
  };

  const heroTitle = (): string => {
    if (pageMode === 'library') return '文件管理库';
    if (isEnterpriseScope()) return auth.organization.name || '空间';
    return personalSpaceHeroTitle();
  };

  const draw = (): void => {
    syncEnterprisePartitions();
    const enterprise = isEnterpriseScope();
    const folderItems = childFolders();
    const splitEnterprise = useEnterpriseZoneLayout();
    const items = splitEnterprise ? activeEnterpriseZoneFiles() : (pageMode === 'home' ? files : visibleFiles());
    const hasMoreFiles = summary.totalCount > summary.shownCount;
    const showTreeSidebar = pageMode === 'library';

    host.innerHTML = `<div class="space-drive-shell ${enterprise ? 'is-enterprise' : 'is-personal'}${showTreeSidebar ? ' is-library' : ''}">
      <header class="space-drive-hero">
        <div class="space-drive-hero-main">
          ${pageMode === 'library' ? `<button type="button" class="space-drive-back-btn" id="spaceBackHomeButton">← 返回空间</button>` : ''}
          <div class="space-drive-hero-copy">
            <div class="space-drive-title-row">
              <h1>${escapeHtml(heroTitle())}</h1>
            </div>
            ${pageMode === 'library' ? `<p>${escapeHtml(`${scopeLabel()} · ${auth.organization.name}`)}</p>` : ''}
          </div>
          <div class="space-drive-hero-stats" aria-label="空间概览">
            <span><strong>${summary.totalCount}</strong> 个文件</span>
            <span><strong>${folders.length || summary.folderCount}</strong> 个文件夹</span>
            <span><strong>${formatSize(summary.totalBytes)}</strong> 占用</span>
          </div>
        </div>
        ${pageMode === 'home' ? renderScopeSwitcher() : ''}
      </header>

      <section class="space-drive-panel${showTreeSidebar ? ' has-tree-sidebar' : ''}">
        ${showTreeSidebar ? renderFolderTreeSidebar() : ''}
        <div class="space-drive-panel-main" data-space-drop-zone>
          <div class="space-drive-drop-hint" aria-hidden="true">
            <strong>松开以上传</strong>
            <span>可同时拖入多个文件夹（在资源管理器中按住 Ctrl 多选）</span>
          </div>
        <div class="space-drive-toolbar${splitEnterprise ? ' has-zone-tabs' : ''}">
          ${renderToolbarLeading()}
          <div class="space-drive-toolbar-actions">
            ${renderToolbarActions()}
          </div>
        </div>

        ${message ? `<div class="space-drive-message" role="status">${escapeHtml(message)}</div>` : ''}

        ${loading ? '<div class="space-drive-empty"><strong>正在加载文件…</strong></div>' : ''}
          ${!loading && !splitEnterprise && !folderItems.length && !items.length ? `<div class="space-drive-empty${showTreeSidebar ? ' space-drive-empty-compact' : ''}">
          <div class="space-drive-empty-icon" aria-hidden="true">📁</div>
          <strong>${pageMode === 'home' ? '还没有最近文件' : '这个文件夹还是空的'}</strong>
            <p>${pageMode === 'home' ? '上传文件或文件夹后，最近的内容会显示在这里。' : '可以新建文件夹整理资料，或直接上传文件/文件夹到当前位置。'}</p>
            ${showTreeSidebar && canCreateFolderIn(currentFolderId) ? '<button type="button" class="space-drive-secondary-btn" id="spaceEmptyNewFolderButton">新建文件夹</button>' : ''}
        </div>` : ''}

        ${!loading && splitEnterprise ? renderEnterpriseZoneContent() : ''}

          ${!loading && !splitEnterprise && pageMode === 'library' && viewMode === 'list' && (folderItems.length || items.length) ? renderLibraryContentList() : ''}
          ${!loading && !splitEnterprise && pageMode === 'home' && items.length && viewMode === 'list' ? `<div class="space-drive-file-list ${enterprise ? 'is-enterprise' : 'is-personal'}">${renderFileListHeader()}${items.map((file, index) => renderFileRow(file, index)).join('')}</div>` : ''}
          ${!loading && !splitEnterprise && pageMode === 'library' && viewMode === 'grid' && (folderItems.length || items.length) ? renderLibraryContentGrid() : ''}

        ${!loading && !splitEnterprise && pageMode === 'home' && hasMoreFiles ? `<div class="space-drive-more">
          <button type="button" class="space-drive-text-btn" id="spaceOpenLibraryMoreButton">查看全部 ${summary.totalCount} 个文件</button>
        </div>` : ''}
        </div>
      </section>
    </div>`;

    bindEvents();
    syncScopeChrome();
  };

  const navigateToFolder = (folderId: string): void => {
    currentFolderId = folderId;
    message = '';
    clearSelection();
    ensureExpandedToFolder(folderId);
    if (pageMode === 'library') void loadWorkspaceData();
    else draw();
  };

  const bindEvents = (): void => {
    host.querySelectorAll<HTMLButtonElement>('[data-enterprise-zone]').forEach(button => button.addEventListener('click', () => {
      const nextTab = button.dataset.enterpriseZone === 'shared' ? 'shared' : 'uploaded';
      if (nextTab === enterpriseZoneTab) return;
      enterpriseZoneTab = nextTab;
      localStorage.setItem(ENTERPRISE_ZONE_STORAGE_KEY, nextTab);
      clearSelection();
      draw();
    }));
    host.querySelectorAll<HTMLButtonElement>('[data-space-scope]').forEach(button => button.addEventListener('click', () => void switchScope(button.dataset.spaceScope === 'enterprise' ? 'enterprise' : 'personal')));
    host.querySelector('#spaceOpenLibraryButton')?.addEventListener('click', () => void openLibrary());
    host.querySelector('#spaceOpenLibraryMoreButton')?.addEventListener('click', () => void openLibrary());
    host.querySelector('#spaceBackHomeButton')?.addEventListener('click', () => {
      pageMode = 'home';
      currentFolderId = '';
      message = '';
      void loadWorkspaceData();
    });
    host.querySelectorAll<HTMLButtonElement>('[data-space-folder]').forEach(button => button.addEventListener('click', () => {
      navigateToFolder(button.dataset.spaceFolder || '');
    }));
    host.querySelectorAll<HTMLButtonElement>('[data-tree-toggle]').forEach(button => button.addEventListener('click', (event) => {
      event.stopPropagation();
      const folderId = button.dataset.treeToggle || '';
      if (!folderId) return;
      if (expandedFolderIds.has(folderId)) expandedFolderIds.delete(folderId);
      else expandedFolderIds.add(folderId);
      draw();
    }));
    host.querySelectorAll<HTMLButtonElement>('[data-space-view]').forEach(button => button.addEventListener('click', () => {
      viewMode = button.dataset.spaceView === 'grid' ? 'grid' : 'list';
      localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
      draw();
    }));
    host.querySelector('#spaceNewFolderButton')?.addEventListener('click', () => {
      if (!canCreateFolderIn(currentFolderId)) {
        window.alert('当前目录不可新建文件夹。');
        return;
      }
      openFolderNameDialog({mode: 'create', parentId: currentFolderId});
    });
    host.querySelector('#spaceTreeNewFolderButton')?.addEventListener('click', () => {
      if (!canCreateFolderIn(currentFolderId)) {
        window.alert('当前目录不可新建文件夹。');
        return;
      }
      openFolderNameDialog({mode: 'create', parentId: currentFolderId});
    });
    host.querySelector('#spaceEmptyNewFolderButton')?.addEventListener('click', () => openFolderNameDialog({mode: 'create', parentId: currentFolderId}));
    host.querySelector('#spaceImportPersonalButton')?.addEventListener('click', () => void openPersonalImportDialog());
    host.querySelector('#spaceMoveButton')?.addEventListener('click', () => void openMoveDialog(movableSelectedFiles()));
    host.querySelector('#spaceDeleteSelectionButton')?.addEventListener('click', () => void bulkDeleteSelection());
    host.querySelector('#spaceClearSelectionButton')?.addEventListener('click', () => {
      clearSelection();
      draw();
    });
    host.querySelector('#spaceShareEnterpriseButton')?.addEventListener('click', () => {
      if (!selectedFileIds.size) return;
      void openShareToEnterpriseDialog([...selectedFileIds]);
    });
    host.querySelector('#spaceUploadEnterpriseButton')?.addEventListener('click', () => {
      if (!selectedFileIds.size) return;
      void openUploadToEnterpriseDialog([...selectedFileIds]);
    });
    host.querySelector('#spaceCancelShareButton')?.addEventListener('click', async () => {
      const targets = sharedSelectedFiles();
      if (!targets.length) {
        window.alert('请选择已共享的文件。');
        return;
      }
      if (!window.confirm(`确定取消 ${targets.length} 个文件的共享吗？团队空间中的对应文件将被移除。`)) return;
      try {
        const result = await api.post<{cancelled: number; skipped: Array<{id: string; reason: string}>}>(apiUrl('/api/files/cancel-share'), {
          fileIds: targets.map(file => file.id),
        });
        clearSelection();
        const skipped = result.skipped?.length ? `，${result.skipped.length} 个未处理` : '';
        message = `已取消 ${result.cancelled ?? 0} 项共享${skipped}`;
        await loadWorkspaceData();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : '取消共享失败');
      }
    });
    host.querySelector<HTMLInputElement>('.space-file-select-all')?.addEventListener('change', (event) => {
      toggleSelectAllVisible((event.target as HTMLInputElement).checked);
    });
    const selectAllInput = host.querySelector<HTMLInputElement>('.space-file-select-all');
    if (selectAllInput) {
      const visibleCount = selectableFiles().length + selectableFolders().length;
      const selectedVisible = selectableFiles().filter(file => selectedFileIds.has(file.id)).length
        + selectableFolders().filter(folder => selectedFolderIds.has(folder.id)).length;
      selectAllInput.indeterminate = selectedVisible > 0 && selectedVisible < visibleCount;
    }
    host.querySelectorAll<HTMLInputElement>('.space-file-select').forEach(input => input.addEventListener('change', () => {
      toggleFileSelection(input.dataset.fileSelect || '', input.checked);
    }));
    host.querySelectorAll<HTMLInputElement>('.space-folder-select').forEach(input => input.addEventListener('change', (event) => {
      event.stopPropagation();
      toggleFolderSelection(input.dataset.folderSelect || '', input.checked);
    }));
    host.querySelectorAll<HTMLButtonElement>('[data-folder-rename]').forEach(button => button.addEventListener('click', async (event) => {
      event.stopPropagation();
      const folderId = button.dataset.folderRename || '';
      const folder = folders.find(item => item.id === folderId);
      if (!folder) return;
      openFolderNameDialog({mode: 'rename', folder});
    }));
    host.querySelectorAll<HTMLButtonElement>('[data-folder-delete]').forEach(button => button.addEventListener('click', async (event) => {
      event.stopPropagation();
      const folderId = button.dataset.folderDelete || '';
      const folder = folders.find(item => item.id === folderId);
      if (!folder) return;
      try {
        await deleteFolder(folder);
        await loadWorkspaceData();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : '删除文件夹失败');
      }
    }));
    host.querySelectorAll<HTMLButtonElement>('[data-folder-menu]').forEach(button => button.addEventListener('click', (event) => {
      event.stopPropagation();
      const folderId = button.dataset.folderMenu || '';
      if (!folderId) return;
      openFolderActionMenu(folderId, button);
    }));
    host.querySelector('.space-drive-library-list')?.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('[data-folder-rename],[data-folder-delete],.space-file-select,.space-folder-select,.space-file-select-all,.space-drive-file-check,.space-download,.space-delete')) return;
      const folderRow = (event.target as HTMLElement).closest<HTMLElement>('.space-drive-folder-row[data-space-folder]');
      if (!folderRow) return;
      navigateToFolder(folderRow.dataset.spaceFolder || '');
    });
    host.querySelector('.space-drive-library-list')?.addEventListener('keydown', (event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;
      if ((event.target as HTMLElement).closest('input,label,button')) return;
      const folderRow = (event.target as HTMLElement).closest<HTMLElement>('.space-drive-folder-row[data-space-folder]');
      if (!folderRow) return;
      event.preventDefault();
      navigateToFolder(folderRow.dataset.spaceFolder || '');
    });
    host.querySelector('.space-drive-library-grid')?.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-space-folder]');
      if (!target || (event.target as HTMLElement).closest('[data-folder-rename],[data-folder-delete],.space-folder-select,.space-drive-file-card-check')) return;
      navigateToFolder(target.dataset.spaceFolder || '');
    });
    host.querySelector('.space-drive-folder-grid')?.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-space-folder]');
      if (!target || (event.target as HTMLElement).closest('[data-folder-rename],[data-folder-delete],.space-folder-select,.space-drive-file-card-check')) return;
      navigateToFolder(target.dataset.spaceFolder || '');
    });
    host.querySelector<HTMLButtonElement>('#spaceUploadButton')?.addEventListener('click', (event) => {
      event.stopPropagation();
      const button = event.currentTarget as HTMLButtonElement;
      if (button.disabled) return;
      openUploadMenu(button);
    });
    const buildUploadSuccessMessage = (mode: 'files' | 'folder', selected: File[], result: {uploaded: number; foldersCreated: number}): string => {
      if (mode === 'folder') {
        const rootCount = countUploadRootFolders(selected);
        if (rootCount > 1) return `已上传 ${rootCount} 个文件夹，共 ${result.uploaded} 个文件`;
        if (result.foldersCreated) return `已上传文件夹，共 ${result.uploaded} 个文件、${result.foldersCreated} 个子目录`;
      }
      return `已上传 ${result.uploaded} 个文件`;
    };
    /**
     * 执行工作区上传（文件或多文件夹）。
     * @param selected 待上传文件
     * @param mode 上传模式
     */
    const runWorkspaceUpload = async (selectedRaw: File[], mode: 'files' | 'folder'): Promise<void> => {
      const selected = filterUploadableFiles(selectedRaw);
      if (!selected?.length) return;
      if (mode === 'folder' && !canCreateFolderIn(currentFolderId)) {
        window.alert('当前目录不可上传文件夹，请进入可整理的目录后再试。');
        return;
      }
      try {
        closeUploadMenu();
        setUploadButtonsBusy(true);
        const result = await uploadWorkspaceFiles(selected);
        message = buildUploadSuccessMessage(mode, selected, result);
        await loadWorkspaceData();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : '上传失败，请稍后重试。');
        draw();
      } finally {
        setUploadButtonsBusy(false);
      }
    };
    const handleWorkspaceUpload = async (input: HTMLInputElement, mode: 'files' | 'folder'): Promise<void> => {
      const selected = Array.from(input.files ?? []);
        input.value = '';
      await runWorkspaceUpload(selected, mode);
    };
    host.querySelector<HTMLInputElement>('#spaceFileInput')?.addEventListener('change', async (event) => {
      await handleWorkspaceUpload(event.target as HTMLInputElement, 'files');
    });
    host.querySelector<HTMLInputElement>('#spaceFolderInput')?.addEventListener('change', async (event) => {
      await handleWorkspaceUpload(event.target as HTMLInputElement, 'folder');
    });

    // 支持从资源管理器 Ctrl 多选文件夹后拖入页面集体上传
    const dropTarget = host.querySelector<HTMLElement>('[data-space-drop-zone]') || host.querySelector<HTMLElement>('.space-drive-panel-main');
    if (dropTarget && pageMode === 'library') {
      let dragDepth = 0;
      const setDropActive = (active: boolean): void => {
        dropTarget.classList.toggle('is-drop-active', active);
      };
      dropTarget.addEventListener('dragenter', event => {
        event.preventDefault();
        dragDepth += 1;
        setDropActive(true);
      });
      dropTarget.addEventListener('dragover', event => {
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        setDropActive(true);
      });
      dropTarget.addEventListener('dragleave', () => {
        dragDepth = Math.max(0, dragDepth - 1);
        if (!dragDepth) setDropActive(false);
      });
      dropTarget.addEventListener('drop', async event => {
        event.preventDefault();
        dragDepth = 0;
        setDropActive(false);
        const transfer = event.dataTransfer;
        if (!transfer) return;
        try {
          const dropped = await collectDroppedFiles(transfer);
          if (!dropped.length) return;
          const hasRelativePath = dropped.some(file => Boolean((file as File & {webkitRelativePath?: string}).webkitRelativePath));
          await runWorkspaceUpload(dropped, hasRelativePath ? 'folder' : 'files');
        } catch (error) {
          window.alert(error instanceof Error ? error.message : '拖放上传失败，请稍后重试。');
        }
      });
    }
    host.querySelectorAll<HTMLButtonElement>('.space-download').forEach(button => button.addEventListener('click', async () => {
      const id = button.closest<HTMLElement>('[data-file-id]')?.dataset.fileId;
      if (!id) return;
      try {
        const result = await api.get<{downloadUrl: string}>(apiUrl(`/api/files/${encodeURIComponent(id)}/download-url`));
        window.open(result.downloadUrl, '_blank', 'noopener');
      } catch (error) {
        window.alert(error instanceof Error ? error.message : '下载失败，请稍后重试。');
      }
    }));
    host.querySelectorAll<HTMLButtonElement>('.space-delete').forEach(button => button.addEventListener('click', async () => {
      const row = button.closest<HTMLElement>('[data-file-id]');
      const id = row?.dataset.fileId;
      const file = files.find(entry => entry.id === id);
      if (!id || !file || !window.confirm(`确定删除「${file.originalName}」吗？`)) return;
      try {
        button.disabled = true;
        const result = await api.delete<{deleteUrl?: string}>(apiUrl(`/api/files/${encodeURIComponent(id)}`));
        if (result.deleteUrl) await fetch(result.deleteUrl, {method: 'DELETE'});
        await loadWorkspaceData();
      } catch (error) {
        button.disabled = false;
        window.alert(error instanceof Error ? error.message : '删除失败，请稍后重试。');
      }
    }));
    host.querySelector('.space-drive-tree-row.is-active')?.scrollIntoView({block: 'nearest'});
  };

  const switchScope = async (nextScope: SpaceScope): Promise<void> => {
    if (nextScope === currentScope()) return;
    const target = nextScope === 'enterprise' ? enterpriseOrg() : personalOrg();
    if (!target) {
      window.alert(nextScope === 'personal' ? '未找到可用的个人空间，请联系管理员。' : '未找到可用的团队空间，请先在「空间设定」中创建或加入团队。');
      return;
    }
    pageMode = 'home';
    if (target.id === auth.organization.id) {
      scope = nextScope;
      localStorage.setItem(SCOPE_STORAGE_KEY, scope);
      currentFolderId = '';
      message = '';
      await loadWorkspaceData();
      return;
    }
    try {
      loading = true;
      draw();
      await api.post(apiUrl(`/api/organizations/enterprise-spaces/${encodeURIComponent(target.id)}/select`), {});
      localStorage.setItem(SCOPE_STORAGE_KEY, nextScope);
      window.location.assign('/tax-assistant/space');
    } catch (error) {
      loading = false;
      message = error instanceof Error ? error.message : '切换空间失败';
      draw();
    }
  };

  const loadHomeData = async (): Promise<void> => {
    const folderResult = await api.get<{folders: SpaceFolder[]}>(apiUrl('/api/files/workspace/folders'));
    folders = folderResult.folders ?? [];
    const fileResult = await api.get<{files: SpaceFile[]; summary?: Partial<SpaceSummary>}>(
      apiUrl(`/api/files?limit=${RECENT_FILE_LIMIT}&summary=1`),
    );
    files = (fileResult.files ?? []).filter(file => file.status !== 'deleted');
    summary = {
      totalCount: Number(fileResult.summary?.totalCount ?? files.length),
      totalBytes: Number(fileResult.summary?.totalBytes ?? 0),
      folderCount: Number(fileResult.summary?.folderCount ?? folders.length),
      shownCount: Number(fileResult.summary?.shownCount ?? files.length),
    };
  };

  const loadLibraryData = async (): Promise<void> => {
    const folderResult = await api.get<{folders: SpaceFolder[]}>(apiUrl('/api/files/workspace/folders'));
    folders = folderResult.folders ?? [];
    const folderQuery = currentFolderId ? `&folderId=${encodeURIComponent(currentFolderId)}` : '';
    const [fileResult, summaryResult] = await Promise.all([
      api.get<{files: SpaceFile[]}>(apiUrl(`/api/files?library=1${folderQuery}`)),
      api.get<{summary?: Partial<SpaceSummary>}>(apiUrl('/api/files?limit=1&summary=1')),
    ]);
    files = (fileResult.files ?? []).filter(file => file.status !== 'deleted');
    const currentFolder = currentFolderId ? folders.find(item => item.id === currentFolderId) : undefined;
    if (currentFolder?.linkedTaskId) {
      const refreshedFolders = await api.get<{folders: SpaceFolder[]}>(apiUrl('/api/files/workspace/folders'));
      folders = refreshedFolders.folders ?? folders;
    }
    summary = {
      totalCount: Number(summaryResult.summary?.totalCount ?? files.length),
      totalBytes: Number(summaryResult.summary?.totalBytes ?? files.reduce((sum, file) => sum + Number(file.sizeBytes || 0), 0)),
      folderCount: folders.length,
      shownCount: files.length,
    };
  };

  const openLibrary = async (): Promise<void> => {
    pageMode = 'library';
    currentFolderId = '';
    message = '';
    expandedFolderIds = new Set<string>([ROOT_TREE_EXPANDED_KEY]);
    await loadWorkspaceData();
  };

  const loadWorkspaceData = async (): Promise<void> => {
    loading = true;
    draw();
    try {
      if (pageMode === 'home') await loadHomeData();
      else await loadLibraryData();
      loading = false;
      draw();
    } catch (error) {
      loading = false;
      const text = error instanceof ApiError ? error.message : error instanceof Error ? error.message : '空间资料加载失败。';
      host.innerHTML = `<section class="space-drive-panel space-drive-error"><h2>空间资料加载失败</h2><p>${escapeHtml(text)}</p><button class="space-drive-secondary-btn" id="spaceRetryButton" type="button">重新加载</button></section>`;
      host.querySelector('#spaceRetryButton')?.addEventListener('click', () => void bootstrap());
    }
  };

  const bulkDeleteFiles = async (targets: SpaceFile[]): Promise<{deleted: number; errors: string[]}> => {
    let deleted = 0;
    const errors: string[] = [];
    for (const file of targets) {
      try {
        const result = await api.delete<{deleteUrl?: string}>(apiUrl(`/api/files/${encodeURIComponent(file.id)}`));
        if (result.deleteUrl) await fetch(result.deleteUrl, {method: 'DELETE'});
        deleted += 1;
      } catch (error) {
        errors.push(`${file.originalName}：${error instanceof Error ? error.message : '删除失败'}`);
      }
    }
    return {deleted, errors};
  };

  /**
   * 删除当前勾选的文件与可删文件夹。
   */
  const bulkDeleteSelection = async (): Promise<void> => {
    const files = deletableSelectedFiles();
    const folderItems = deletableSelectedFolders();
    if (!files.length && !folderItems.length) {
      window.alert('所选项目均不可删除。系统目录或被任务占用的文件夹不能删除。');
      return;
    }
    const parts = [
      folderItems.length ? `${folderItems.length} 个文件夹` : '',
      files.length ? `${files.length} 个文件` : '',
    ].filter(Boolean);
    if (!window.confirm(`确定删除选中的 ${parts.join('、')} 吗？文件夹删除规则与单项删除相同。`)) return;
    const fileResult = await bulkDeleteFiles(files);
    const folderErrors: string[] = [];
    let foldersDeleted = 0;
    for (const folder of folderItems) {
      try {
        await deleteFolder(folder, {confirm: false});
        foldersDeleted += 1;
      } catch (error) {
        folderErrors.push(`${folder.name}：${error instanceof Error ? error.message : '删除失败'}`);
      }
    }
    clearSelection();
    const errors = [...fileResult.errors, ...folderErrors];
    const deletedParts = [
      foldersDeleted ? `${foldersDeleted} 个文件夹` : '',
      fileResult.deleted ? `${fileResult.deleted} 个文件` : '',
    ].filter(Boolean);
    message = errors.length
      ? `已删除 ${deletedParts.join('、') || '0 项'}，${errors.length} 个失败`
      : `已删除 ${deletedParts.join('、')}`;
    if (errors.length) window.alert(errors.join('\n'));
    await loadWorkspaceData();
  };

  /**
   * 打开移动文件对话框
   * @param targets 待移动文件
   */
  const openMoveDialog = async (targets: SpaceFile[]): Promise<void> => {
    const movable = targets.filter(canMoveFile);
    if (!movable.length) {
      window.alert('请选择可移动的文件。任务生成、识别原件或被方案引用的模版不能移动。');
      return;
    }
    const options = folderPickerOptions().filter(option => option.id !== currentFolderId);
    if (!options.length) {
      window.alert('没有可用的目标文件夹。');
      return;
    }
    const overlay = document.createElement('div');
    overlay.className = 'space-drive-import-overlay';
    overlay.innerHTML = `<div class="space-drive-import-dialog" role="dialog" aria-modal="true" aria-label="移动文件">
      <div class="space-drive-import-head"><h2>移动 ${movable.length} 个文件</h2><p>选择目标文件夹，文件将从当前位置移出。</p></div>
      <label class="space-drive-form-field">目标文件夹
        <select id="spaceMoveFolderSelect" class="space-drive-select">${options.map(option => `<option value="${escapeHtml(option.id)}" ${option.disabled ? 'disabled' : ''}>${escapeHtml(option.label)}${option.disabled ? '（专属目录）' : ''}</option>`).join('')}</select>
      </label>
      <div class="space-drive-import-actions"><button type="button" class="space-drive-secondary-btn" data-move-cancel>取消</button><button type="button" class="space-drive-primary-btn" data-move-confirm>确认移动</button></div>
    </div>`;
    document.body.append(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('[data-move-cancel]')?.addEventListener('click', close);
    overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
    overlay.querySelector('[data-move-confirm]')?.addEventListener('click', async () => {
      const folderId = overlay.querySelector<HTMLSelectElement>('#spaceMoveFolderSelect')?.value ?? '';
      const button = overlay.querySelector<HTMLButtonElement>('[data-move-confirm]');
      if (button) {
        button.disabled = true;
        button.textContent = '移动中…';
      }
      try {
        const result = await api.post<{moved: SpaceFile[]; skipped: Array<{id: string; reason: string}>}>(apiUrl('/api/files/workspace/move'), {
          fileIds: movable.map(file => file.id),
          folderId,
        });
        close();
        clearSelection();
        const skipped = result.skipped?.length ? `，${result.skipped.length} 个未移动` : '';
        message = `已移动 ${result.moved?.length ?? 0} 个文件${skipped}`;
        await loadWorkspaceData();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : '移动失败');
        if (button) {
          button.disabled = false;
          button.textContent = '确认移动';
        }
      }
    });
  };

  /**
   * 打开共享至团队空间对话框
   * @param presetIds 预选文件 ID
   */
  const openShareToEnterpriseDialog = async (presetIds: string[] = []): Promise<void> => {
    const enterprises = enterpriseOrgs();
    if (!enterprises.length) {
      window.alert('尚未加入团队空间，无法共享。');
      return;
    }
    const candidates = presetIds.length
      ? files.filter(file => presetIds.includes(file.id))
      : files.filter(canShareFile);
    const shareable = candidates.filter(canShareFile);
    if (!shareable.length) {
      window.alert('所选文件无法共享。识别原件不能共享；请确认文件属于您本人。');
      return;
    }
    const selected = new Set(shareable.map(file => file.id));
    const overlay = document.createElement('div');
    overlay.className = 'space-drive-import-overlay';
    overlay.innerHTML = `<div class="space-drive-import-dialog" role="dialog" aria-modal="true" aria-label="共享至团队空间">
      <div class="space-drive-import-head"><h2>共享至团队「共享」文件夹</h2><p>文件将在团队空间显示为只读副本，个人空间保留原文件。任务成果、台账导出及方案模版快照均可共享；识别原件不可共享。你可随时取消共享，删除个人文件时团队侧副本也会移除。</p></div>
      ${enterprises.length > 1 ? `<label class="space-drive-form-field">目标团队
        <select id="spaceShareEnterpriseSelect" class="space-drive-select">${enterprises.map(org => `<option value="${escapeHtml(org.id)}">${escapeHtml(org.name)}</option>`).join('')}</select>
      </label>` : `<p class="space-drive-share-target">目标团队：<strong>${escapeHtml(enterprises[0]?.name ?? '')}</strong></p>`}
      <div class="space-drive-import-list">${shareable.map(file => `<label class="space-drive-import-item"><input type="checkbox" value="${escapeHtml(file.id)}" checked><span><strong>${escapeHtml(file.originalName)}</strong><small>${escapeHtml(formatSize(file.sizeBytes))} · ${escapeHtml(sourceLabel(file))}</small></span></label>`).join('')}</div>
      <div class="space-drive-import-actions"><button type="button" class="space-drive-secondary-btn" data-share-cancel>取消</button><button type="button" class="space-drive-primary-btn" data-share-confirm>确认共享</button></div>
    </div>`;
    document.body.append(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('[data-share-cancel]')?.addEventListener('click', close);
    overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
    overlay.querySelector('[data-share-confirm]')?.addEventListener('click', async () => {
      overlay.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').forEach(input => selected.add(input.value));
      const checkedIds = [...selected].filter(id => shareable.some(file => file.id === id));
      if (!checkedIds.length) {
        window.alert('请至少选择一个文件。');
        return;
      }
      const enterpriseId = overlay.querySelector<HTMLSelectElement>('#spaceShareEnterpriseSelect')?.value || enterprises[0]?.id || '';
      const button = overlay.querySelector<HTMLButtonElement>('[data-share-confirm]');
      if (button) {
        button.disabled = true;
        button.textContent = '共享中…';
      }
      try {
        const result = await api.post<{transferred: SpaceFile[]; skipped: Array<{id: string; reason: string}>; enterpriseName?: string}>(apiUrl('/api/files/share-to-enterprise'), {
          fileIds: checkedIds,
          enterpriseOrganizationId: enterpriseId,
        });
        close();
        clearSelection();
        const skipped = result.skipped?.length ? `，${result.skipped.length} 个未共享` : '';
        message = `已共享 ${result.transferred?.length ?? 0} 个文件至${result.enterpriseName ? `「${result.enterpriseName}」` : '团队空间'}的「共享」文件夹${skipped}`;
        await loadWorkspaceData();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : '共享失败');
        if (button) {
          button.disabled = false;
          button.textContent = '确认共享';
        }
      }
    });
  };

  /**
   * 打开上传至团队空间对话框
   * @param presetIds 预选文件 ID
   */
  const openUploadToEnterpriseDialog = async (presetIds: string[] = []): Promise<void> => {
    const enterprises = enterpriseOrgs();
    if (!enterprises.length) {
      window.alert('尚未加入团队空间，无法上传。');
      return;
    }
    const candidates = presetIds.length
      ? files.filter(file => presetIds.includes(file.id))
      : files.filter(canUploadToEnterprise);
    const uploadable = candidates.filter(canUploadToEnterprise);
    if (!uploadable.length) {
      window.alert('请选择可上传的文件。任务生成或识别原件不能上传至团队空间。');
      return;
    }
    const selected = new Set(uploadable.map(file => file.id));
    let enterpriseFolders: SpaceFolder[] = await loadEnterpriseFolders(enterprises[0]?.id ?? '');
    const overlay = document.createElement('div');
    overlay.className = 'space-drive-import-overlay';
    const renderFolderSelect = (folderList: SpaceFolder[]): string => folderPickerOptions(folderList)
      .filter(option => !option.disabled)
      .map(option => `<option value="${escapeHtml(option.id)}">${escapeHtml(option.label)}</option>`)
      .join('');
    overlay.innerHTML = `<div class="space-drive-import-dialog" role="dialog" aria-modal="true" aria-label="上传至团队空间">
      <div class="space-drive-import-head"><h2>上传至团队空间</h2><p>将个人文件复制到团队空间指定文件夹。个人与团队两侧互不影响，删除个人文件不会移除团队副本。</p></div>
      ${enterprises.length > 1 ? `<label class="space-drive-form-field">目标团队
        <select id="spaceUploadEnterpriseSelect" class="space-drive-select">${enterprises.map(org => `<option value="${escapeHtml(org.id)}">${escapeHtml(org.name)}</option>`).join('')}</select>
      </label>` : `<p class="space-drive-share-target">目标团队：<strong>${escapeHtml(enterprises[0]?.name ?? '')}</strong></p>`}
      <label class="space-drive-form-field">目标文件夹
        <select id="spaceUploadFolderSelect" class="space-drive-select">${renderFolderSelect(enterpriseFolders)}</select>
      </label>
      <div class="space-drive-import-list">${uploadable.map(file => `<label class="space-drive-import-item"><input type="checkbox" value="${escapeHtml(file.id)}" checked><span><strong>${escapeHtml(file.originalName)}</strong><small>${escapeHtml(formatSize(file.sizeBytes))} · ${escapeHtml(sourceLabel(file))}</small></span></label>`).join('')}</div>
      <div class="space-drive-import-actions"><button type="button" class="space-drive-secondary-btn" data-upload-cancel>取消</button><button type="button" class="space-drive-primary-btn" data-upload-confirm>确认上传</button></div>
    </div>`;
    document.body.append(overlay);
    const close = () => overlay.remove();
    const folderSelect = () => overlay.querySelector<HTMLSelectElement>('#spaceUploadFolderSelect');
    overlay.querySelector('#spaceUploadEnterpriseSelect')?.addEventListener('change', async (event) => {
      const enterpriseId = (event.target as HTMLSelectElement).value;
      enterpriseFolders = await loadEnterpriseFolders(enterpriseId);
      const select = folderSelect();
      if (select) select.innerHTML = renderFolderSelect(enterpriseFolders);
    });
    overlay.querySelector('[data-upload-cancel]')?.addEventListener('click', close);
    overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
    overlay.querySelector('[data-upload-confirm]')?.addEventListener('click', async () => {
      overlay.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').forEach(input => selected.add(input.value));
      const checkedIds = [...selected].filter(id => uploadable.some(file => file.id === id));
      if (!checkedIds.length) {
        window.alert('请至少选择一个文件。');
        return;
      }
      const enterpriseId = overlay.querySelector<HTMLSelectElement>('#spaceUploadEnterpriseSelect')?.value || enterprises[0]?.id || '';
      const folderId = folderSelect()?.value ?? '';
      const button = overlay.querySelector<HTMLButtonElement>('[data-upload-confirm]');
      if (button) {
        button.disabled = true;
        button.textContent = '上传中…';
      }
      try {
        const result = await api.post<{transferred: SpaceFile[]; skipped: Array<{id: string; reason: string}>; enterpriseName?: string}>(apiUrl('/api/files/upload-to-enterprise'), {
          fileIds: checkedIds,
          enterpriseOrganizationId: enterpriseId,
          folderId,
        });
        close();
        clearSelection();
        const skipped = result.skipped?.length ? `，${result.skipped.length} 个未上传` : '';
        message = `已上传 ${result.transferred?.length ?? 0} 个文件至${result.enterpriseName ? `「${result.enterpriseName}」` : '团队空间'}${skipped}`;
        await loadWorkspaceData();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : '上传失败');
        if (button) {
          button.disabled = false;
          button.textContent = '确认上传';
        }
      }
    });
  };

  const openPersonalImportDialog = async (): Promise<void> => {
    try {
      const result = await api.get<{files: SpaceFile[]}>(apiUrl('/api/files?scope=workspace&source=personal'));
      const candidates = (result.files ?? []).filter(file => file.uploadedBy === auth.user.id);
      if (!candidates.length) {
        window.alert('个人空间中没有可移入的文件。');
        return;
      }
      const selected = new Set<string>();
      const overlay = document.createElement('div');
      overlay.className = 'space-drive-import-overlay';
      overlay.innerHTML = `<div class="space-drive-import-dialog" role="dialog" aria-modal="true" aria-label="从个人空间移入">
        <div class="space-drive-import-head"><h2>从个人空间移入</h2><p>选择要复制到团队空间的文件，移入后个人空间中的原文件将被移除。</p></div>
        <div class="space-drive-import-list">${candidates.map(file => `<label class="space-drive-import-item"><input type="checkbox" value="${escapeHtml(file.id)}"><span><strong>${escapeHtml(file.originalName)}</strong><small>${escapeHtml(formatSize(file.sizeBytes))} · ${escapeHtml(formatDate(file.createdAt))}</small></span></label>`).join('')}</div>
        <div class="space-drive-import-actions"><button type="button" class="space-drive-secondary-btn" data-import-cancel>取消</button><button type="button" class="space-drive-primary-btn" data-import-confirm>移入选中文件</button></div>
      </div>`;
      document.body.append(overlay);
      const close = () => overlay.remove();
      overlay.querySelector('[data-import-cancel]')?.addEventListener('click', close);
      overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
      overlay.querySelector('[data-import-confirm]')?.addEventListener('click', async () => {
        overlay.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').forEach(input => selected.add(input.value));
        if (!selected.size) {
          window.alert('请至少选择一个文件。');
          return;
        }
        const button = overlay.querySelector<HTMLButtonElement>('[data-import-confirm]');
        if (button) {
          button.disabled = true;
          button.textContent = '移入中…';
        }
        try {
          const transfer = await api.post<{transferred: SpaceFile[]; skipped: Array<{id: string; reason: string}>}>(apiUrl('/api/files/transfer-to-enterprise'), {
            fileIds: [...selected],
            folderId: currentFolderId,
          });
          close();
          const skipped = transfer.skipped?.length ? `，${transfer.skipped.length} 个文件未移入` : '';
          message = `已移入 ${transfer.transferred?.length ?? 0} 个文件${skipped}`;
          await loadWorkspaceData();
        } catch (error) {
          window.alert(error instanceof Error ? error.message : '移入失败');
          if (button) {
            button.disabled = false;
            button.textContent = '移入选中文件';
          }
        }
      });
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '读取个人空间文件失败');
    }
  };

  const bootstrap = async (): Promise<void> => {
    try {
      const latestAuth = await currentAuth();
      if (latestAuth) auth = latestAuth;
      const savedScope = localStorage.getItem(SCOPE_STORAGE_KEY) as SpaceScope | null;
      const result = await api.get<{organizations: UserOrganization[]}>(apiUrl('/api/organizations'));
      organizations = result.organizations ?? [];
      scope = scopeFromOrganization(auth.organization);
      localStorage.setItem(SCOPE_STORAGE_KEY, scope);

      const preferredTarget = savedScope === 'enterprise'
        ? enterpriseOrg()
        : savedScope === 'personal'
          ? personalOrg()
          : undefined;
      if (
        savedScope
        && savedScope !== scope
        && preferredTarget
        && preferredTarget.id !== auth.organization.id
        && canSwitchScope()
      ) {
        await switchScope(savedScope);
        return;
      }

      const deepLinkParams = new URLSearchParams(window.location.search);
      const deepLinkScope = deepLinkParams.get('scope');
      const deepLinkFolder = deepLinkParams.get('folder');
      if (deepLinkScope === 'enterprise' && enterpriseOrgs().length && currentScope() !== 'enterprise') {
        await switchScope('enterprise');
        return;
      }
      await loadWorkspaceData();
      if (deepLinkFolder && folders.some(item => item.id === deepLinkFolder)) {
        currentFolderId = deepLinkFolder;
        pageMode = 'library';
        ensureExpandedToFolder(deepLinkFolder);
        draw();
      }
    } catch (error) {
      const text = error instanceof ApiError ? error.message : error instanceof Error ? error.message : '空间资料加载失败。';
      host.innerHTML = `<section class="space-drive-panel space-drive-error"><h2>空间资料加载失败</h2><p>${escapeHtml(text)}</p><button class="space-drive-secondary-btn" id="spaceRetryButton" type="button">重新加载</button></section>`;
      host.querySelector('#spaceRetryButton')?.addEventListener('click', () => void bootstrap());
    }
  };

  await bootstrap();
}
